"use client";

import { AnimatedCursor, getCursorTarget } from "./animated-cursor";
import { springSnappy } from "./motion-tokens";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { useEffect, useState, useRef } from "react";

type ArrangementItem = {
  content: string;
  preset: "default" | "warning" | "info" | "important";
};

const PRESET_BUTTONS = ["Intro", "Verse", "Chorus", "Bridge", "Outro"];

const SEQUENCE: ArrangementItem[] = [
  { content: "Intro - keys only", preset: "info" },
  { content: "Verse 1", preset: "default" },
  { content: "Chorus - full band", preset: "important" },
  { content: "Bridge - half time", preset: "warning" },
  { content: "Outro - fade", preset: "default" },
];

function getItemClasses(preset: ArrangementItem["preset"]): string {
  switch (preset) {
    case "warning":
      return "border bg-red-500/20 text-red-800 dark:text-red-300 border-red-500/30";
    case "info":
      return "border bg-blue-500/20 text-blue-800 dark:text-blue-300 border-blue-500/30";
    case "important":
      return "border bg-amber-500/20 text-amber-900 dark:text-amber-200 border-amber-500/30";
    default:
      return "border bg-surface-secondary border-border-primary";
  }
}

export function ArrangementDemo() {
  const [items, setItems] = useState<ArrangementItem[]>([]);
  const [cursorPos, setCursorPos] = useState({ x: 60, y: 50 });
  const [isClicking, setIsClicking] = useState(false);
  const [activePreset, setActivePreset] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { amount: 0.3 });

  useEffect(() => {
    if (!isInView) return;
    let mounted = true;

    const runSequence = async () => {
      setItems([]);
      await sleep(1000);

      for (let i = 0; i < SEQUENCE.length; i++) {
        if (!mounted) return;

        const buttonIndex = i % PRESET_BUTTONS.length;
        const targetId = `arrangement-btn-${buttonIndex}`;
        const pos = getCursorTarget(targetId, "arrangement-demo-container");
        if (!pos) {
          await sleep(500);
          continue;
        }

        setCursorPos(pos);
        await sleep(500);
        if (!mounted) return;

        setIsClicking(true);
        setActivePreset(buttonIndex);
        await sleep(120);
        if (!mounted) return;

        setIsClicking(false);
        const item = SEQUENCE[i];
        if (item) {
          setItems((prev) => [...prev, item]);
        }

        await sleep(200);
        setActivePreset(null);
        await sleep(800);
        if (!mounted) return;
      }

      await sleep(3000);
      if (!mounted) return;
      void runSequence();
    };

    void runSequence();
    return () => {
      mounted = false;
    };
  }, [isInView]);

  return (
    <div
      ref={containerRef}
      id="arrangement-demo-container"
      className="relative w-full max-w-md mx-auto min-h-[340px]"
    >
      <div className="rounded-xl border border-border-primary bg-surface-secondary overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="px-3 py-2 border-b border-border-primary bg-surface-primary">
          <span className="text-[10px] uppercase tracking-wider font-medium text-glyphs-secondary">
            Arrangement
          </span>
        </div>

        {/* Add item panel */}
        <div className="p-2.5 bg-surface-secondary border-b border-border-primary">
          <span className="text-[10px] font-medium text-glyphs-secondary mb-1.5 block">
            Add Item
          </span>
          <div className="flex flex-wrap gap-1.5">
            {PRESET_BUTTONS.map((label, i) => (
              <motion.button
                key={label}
                id={`arrangement-btn-${i}`}
                className="text-[10px] h-6 px-2.5 rounded border border-border-primary font-medium text-glyphs-primary bg-surface-secondary"
                animate={{
                  scale: activePreset === i ? 0.92 : 1,
                  backgroundColor:
                    activePreset === i
                      ? "var(--color-theme-blue)"
                      : "var(--color-surface-secondary)",
                  color:
                    activePreset === i
                      ? "white"
                      : "var(--color-glyphs-primary)",
                }}
                transition={springSnappy}
              >
                {label}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Arrangement items list */}
        <div className="p-2 space-y-1.5 min-h-[190px]">
          <AnimatePresence>
            {items.map((item, i) => (
              <motion.div
                key={`${item.content}-${i}`}
                className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md font-mono text-xs ${getItemClasses(item.preset)}`}
                initial={{ opacity: 0, height: 0, y: -8 }}
                animate={{ opacity: 1, height: "auto", y: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 28,
                }}
              >
                <span className="text-glyphs-secondary text-[10px] font-sans">
                  {i + 1}.
                </span>
                {item.content}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      <AnimatedCursor
        x={cursorPos.x}
        y={cursorPos.y}
        visible={isInView}
        clicking={isClicking}
      />
    </div>
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
